import { ANALYTICS_EVENTS, useEvent } from '../../../common'
import { CategoryGuidToName } from '../../../core/constants'
import { DetailedCategory } from '../../../core'
import { TRENDS_ANALYTICS_METADATA } from '../../constants/TrendsWidget'
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro'
import { useTheme } from '@mui/material/styles'
import { TrendsTableRow } from '../../utils/TrendsTable'
import { AccessibleSortIcon } from './AccessibleSortIcon'

interface TrendsTableProps {
  columns: GridColDef[]
  onRowClick?: (category: string) => void
  parentCategory?: DetailedCategory
  rows: TrendsTableRow[]
  totalRow?: TrendsTableRow
}

export const TrendsTable = ({
  columns,
  onRowClick,
  parentCategory,
  rows,
  totalRow,
}: TrendsTableProps) => {
  const theme = useTheme()
  const { onEvent } = useEvent()
  const isLightMode = theme.palette.mode === 'light'

  return (
    <DataGridPro
      // autoHeight sizes the grid to its content instead of measuring a flex
      // parent. The parent is an overflow:auto Stack with no fixed height, so the
      // default virtualized layout makes x-data-grid's useGridDimensions loop on
      // its own ResizeObserver measurement (a feedback loop that spikes to React's
      // "Maximum update depth exceeded" under a starved CI worker in jsdom). Content
      // sizing removes the measurement feedback and is correct for this unbounded,
      // page-scrolling table.
      autoHeight={true}
      columns={columns}
      disableColumnFilter={true}
      disableColumnMenu={true}
      hideFooter={true}
      initialState={{ sorting: { sortModel: [{ field: 'category', sort: 'asc' }] } }}
      onRowClick={(r) => {
        if (!parentCategory) {
          onRowClick?.(r.row.category_guid)
        }
        onEvent(ANALYTICS_EVENTS.TRENDS_CLICK_LIST_ITEM, {
          ...TRENDS_ANALYTICS_METADATA,
          listItem: CategoryGuidToName[r.row.category_guid],
        })
      }}
      pinnedRows={{
        bottom: totalRow ? [totalRow] : [],
      }}
      rowHeight={64}
      rows={rows}
      slotProps={{ baseIconButton: { color: 'secondary', sx: { ml: 8 } } }}
      slots={{ columnHeaderSortIcon: AccessibleSortIcon }}
      sortingOrder={['asc', 'desc']}
      sx={{
        borderColor: 'divider',
        borderWidth: 1,
        borderStyle: 'solid',
        borderRadius: 2,
        '& .MuiDataGrid-columnHeaders': {
          bgcolor: isLightMode ? 'neutral.50' : 'neutral.800',
          borderBottom: 'none',
        },
        '& .trends-table-header': {
          bgcolor: isLightMode ? 'neutral.50' : 'neutral.800',
        },
      }}
    />
  )
}
